import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import type { Patient } from '@/types/patients';
import { PatientPhotoEditor } from './PatientPhotoEditor';
import { PatientPortalLinkButton } from './PatientPortalLinkButton';
import { ageFromDob, sexLabelKey } from './patientDisplay';

interface PatientHeaderBandProps {
  patient: Patient;
  onEdit: () => void;
  /**
   * Opens the doctor's consultation form. Absent means the surface offers no
   * way to start one, and the button is not rendered at all — a disabled
   * button would advertise something the clinic's workflow no longer has.
   */
  onStartConsultation?: () => void;
  /** Reserved for a future delete affordance (not built in Phase 1). */
  canDelete: boolean;
  /** Phase 1: the consult screen does not exist yet, so Start is disabled. */
  startDisabled?: boolean;
  /** Accessible title explaining why Start is disabled. */
  startTitle?: string;
}

/**
 * Patient chart header: avatar, name, demographics. The NIC shown here comes
 * from `patient.nationalId`, which IS present on the single-patient GET (the
 * server decrypts extras for the detail endpoint, unlike search).
 *
 * Desktop shows Edit and, when `onStartConsultation` is supplied, Start
 * consultation; mobile shows only an edit icon — the sticky Start CTA lives in
 * the page on mobile.
 */
export function PatientHeaderBand({
  patient,
  onEdit,
  onStartConsultation,
  startDisabled,
  startTitle,
}: PatientHeaderBandProps) {
  const { t } = useTranslation();
  const age = ageFromDob(patient.dateOfBirth);
  const sex = t(sexLabelKey(patient.sex));

  return (
    <div className="flex items-start gap-3.5">
      <PatientPhotoEditor patient={patient} size={48} />
      <div className="min-w-0 flex-1">
        <div className="text-[20px] font-bold leading-tight tracking-[-0.02em] md:text-2xl">
          {patient.fullName}
        </div>
        <div className="mt-1 text-[13px] text-muted">
          {age != null && (
            <>
              {age} {t('patients.yrs')} ·{' '}
            </>
          )}
          {sex}
          {patient.nationalId && (
            <>
              {' · '}
              <span className="font-mono">
                {t('patients.nicLabel')} {patient.nationalId}
              </span>
            </>
          )}
        </div>
        {(patient.phone || patient.address) && (
          <div className="mt-0.5 font-mono text-[12.5px] text-muted">
            {[patient.phone, patient.address].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      {/* Desktop actions */}
      <div className="hidden shrink-0 gap-2 md:flex">
        <PatientPortalLinkButton patient={patient} />
        <Button variant="secondary" onClick={onEdit}>
          <Icon name="edit" size={16} /> {t('patients.editDetails')}
        </Button>
        {onStartConsultation && (
          <Button onClick={onStartConsultation} disabled={startDisabled} title={startTitle}>
            <Icon name="arrow_up_right" size={16} /> {t('patients.startConsultation')}
          </Button>
        )}
      </div>

      {/* Mobile actions (Start CTA is the page's sticky bar) */}
      <div className="flex shrink-0 gap-2 md:hidden">
        <PatientPortalLinkButton patient={patient} iconOnly />
      </div>
      <button
        type="button"
        onClick={onEdit}
        aria-label={t('patients.editDetails')}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line-2 bg-paper text-ink-2 hover:bg-paper-2 md:hidden"
      >
        <Icon name="edit" size={16} />
      </button>
    </div>
  );
}
